import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Edit3, Trash2, Plus, Save } from "lucide-react";

/* Shared Modal */
export function AdminModal({ open, onClose, title, children, size = "max-w-3xl", testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className={`bg-white rounded-2xl ${size} w-full max-h-[90vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg text-[#102A43]">{title}</h3>
          <button onClick={onClose} className="p-1 text-[#4B5563] hover:text-[#102A43]" data-testid="modal-close"><X className="w-5 h-5"/></button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
      </div>
    </div>
  );
}

export function AdminField({ label, name, value, onChange, type = "text", required, isTextArea, testid, rows = 4 }) {
  const cls = "w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[#102A43] focus:border-[#1F8A5B] focus:outline-none focus:ring-2 focus:ring-[#1F8A5B]/10 text-sm";
  return (
    <div>
      <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">{label}</label>
      {isTextArea ? (
        <textarea name={name} value={value || ""} onChange={onChange} required={required} rows={rows} className={cls} data-testid={testid}/>
      ) : (
        <input type={type} name={name} value={value || ""} onChange={onChange} required={required} className={cls} data-testid={testid}/>
      )}
    </div>
  );
}

/* File upload widget */
export function ImageUploadField({ label, value, onChange, testid }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Save the RELATIVE path (e.g. "/api/files/abc") — NOT the absolute URL.
      // This prevents the image from breaking when the backend URL changes
      // (e.g. after a preview → production deploy).
      onChange(data.url);
      toast.success("Yüklendi");
    } catch (e) {
      toast.error("Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };
  const preview = fileUrl(value);
  return (
    <div>
      <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-3">
        {preview && <img src={preview} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#E5E7EB]"/>}
        <input ref={inputRef} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid={testid}/>
        <button type="button" onClick={() => inputRef.current?.click()} className="btn-outline !py-2 !px-3 text-xs" data-testid={`${testid}-btn`}>
          <Upload className="w-3.5 h-3.5"/> {uploading ? "Yükleniyor..." : "Yükle"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs text-red-600 hover:text-red-800">Kaldır</button>
        )}
      </div>
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="veya URL yapıştırın" className="mt-2 w-full px-3 py-2 rounded-lg border border-[#E5E7EB] bg-white text-[#102A43] focus:border-[#1F8A5B] focus:outline-none text-xs" data-testid={`${testid}-url`}/>
    </div>
  );
}

/* Table pattern */
export function AdminTable({ columns, rows, onEdit, onDelete, testid, keyField = "id" }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F9FA] border-b border-[#E5E7EB]">
            <tr>
              {columns.map(c => <th key={c.key} className="text-start px-4 py-3 font-medium text-[#4B5563] text-xs uppercase tracking-wider">{c.label}</th>)}
              {(onEdit || onDelete) && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="py-10 text-center text-[#4B5563]">Kayıt yok</td></tr>
            ) : rows.map(r => (
              <tr key={r[keyField]} className="border-b border-[#E5E7EB] last:border-0 hover:bg-[#F7F9FA]/50">
                {columns.map(c => (
                  <td key={c.key} className="px-4 py-3 text-[#102A43]">{c.render ? c.render(r) : r[c.key]}</td>
                ))}
                {(onEdit || onDelete) && (
                  <td className="px-4 py-3 text-end">
                    <div className="flex items-center justify-end gap-1">
                      {onEdit && (
                        <button onClick={() => onEdit(r)} className="p-1.5 text-[#1F8A5B] hover:bg-[#EAF7F0] rounded" data-testid={`edit-${r[keyField]}`}><Edit3 className="w-4 h-4"/></button>
                      )}
                      {onDelete && (
                        <button onClick={() => { if (window.confirm("Silmek istediğinize emin misiniz?")) onDelete(r); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-${r[keyField]}`}><Trash2 className="w-4 h-4"/></button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminPageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#102A43]">{title}</h1>
        {subtitle && <p className="text-sm text-[#4B5563] mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/* Slug util */
export const toSlug = (str) => (str || "").toString().toLowerCase()
  .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
  .replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");
